import { motion, AnimatePresence } from "motion/react";
import { Search, Building2, Globe, ArrowRight, Lock } from "lucide-react";
import { useState } from "react";

import { useRWAStore, Asset } from "../store/rwa-store";

interface MarketplaceViewProps {
    onAssetClick?: (assetId: string) => void;
}

export default function MarketplaceView({ onAssetClick }: MarketplaceViewProps) {
    const { assets, currentUser, updatePortfolio, deposit } = useRWAStore();
    const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
    const [investmentAmount, setInvestmentAmount] = useState<number>(0);
    const [processing, setProcessing] = useState(false);
    const [filter, setFilter] = useState<'all' | 'crypto' | 'rwa'>('all');

    // Filter Logic
    const filteredAssets = assets.filter(a => filter === 'all' ? true : a.type === filter);

    const handleInvest = () => {
        setProcessing(true);
        setTimeout(() => {
            if (selectedAsset && investmentAmount > 0) {
                // Convert currency to asset amount approx
                const qty = investmentAmount / selectedAsset.price;
                // Add to portfolio (Naive addition)
                updatePortfolio(selectedAsset.id, qty, selectedAsset.price);
                // Deduct from wallet (Naive deduction simulation)
                deposit(-investmentAmount, 'USD');

                setSelectedAsset(null);
                setInvestmentAmount(0);
            }
            setProcessing(false);
        }, 1500);
    };

    return (
        <div className="max-w-7xl mx-auto">
            {/* HEADER */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-bold">Primary Market</h1>
                    <p className="text-neutral-400">Subscription opportunities and token listings.</p>
                </div>

                <div className="flex gap-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-3 w-4 h-4 text-neutral-500" />
                        <input className="pl-10 pr-4 py-2.5 bg-neutral-900 border border-neutral-800 rounded-lg text-sm w-64 focus:border-neutral-700 focus:outline-hidden" placeholder="Search assets..." />
                    </div>
                    <div className="flex bg-neutral-900 border border-neutral-800 rounded-lg p-1">
                        <button onClick={() => setFilter('all')} className={`px-3 py-1.5 text-sm font-medium rounded ${filter === 'all' ? 'bg-neutral-800 text-white' : 'text-neutral-500'}`}>All</button>
                        <button onClick={() => setFilter('rwa')} className={`px-3 py-1.5 text-sm font-medium rounded ${filter === 'rwa' ? 'bg-neutral-800 text-white' : 'text-neutral-500'}`}>RWA</button>
                        <button onClick={() => setFilter('crypto')} className={`px-3 py-1.5 text-sm font-medium rounded ${filter === 'crypto' ? 'bg-neutral-800 text-white' : 'text-neutral-500'}`}>Crypto</button>
                    </div>
                </div>
            </div>

            {/* ASSET GRID */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredAssets.map(asset => (
                    <div key={asset.id} className="group bg-neutral-900/50 border border-neutral-800 rounded-2xl overflow-hidden hover:border-emerald-500/30 transition-all hover:bg-neutral-900 duration-300">
                        {/* CARD HEADER */}
                        <div className="p-6 border-b border-neutral-800/50">
                            <div className="flex justify-between items-start mb-4">
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${asset.type === 'rwa' ? 'bg-blue-500/10 text-blue-500' : 'bg-orange-500/10 text-orange-500'}`}>
                                    {asset.type === 'rwa' ? <Building2 className="w-6 h-6" /> : <Globe className="w-6 h-6" />}
                                </div>
                                <div className={`px-2 py-1 rounded border text-xs font-mono uppercase ${asset.change24h >= 0 ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'bg-red-500/10 text-red-500 border-red-500/20'}`}>
                                    {asset.change24h >= 0 ? '+' : ''}{asset.change24h}%
                                </div>
                            </div>
                            <h3 className="text-lg font-bold truncate">{asset.name}</h3>
                            <div className="flex items-center gap-2 text-sm text-neutral-500">
                                <span className="font-mono bg-neutral-950 px-1.5 rounded">{asset.symbol}</span>
                                <span>•</span>
                                <span className="capitalize">{asset.type} Class</span>
                            </div>
                        </div>

                        {/* CARD METRICS */}
                        <div className="p-6 grid grid-cols-2 gap-4">
                            <div>
                                <div className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Price</div>
                                <div className="font-mono font-medium">${asset.price.toLocaleString()}</div>
                            </div>
                            <div>
                                <div className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Min Ticket</div>
                                <div className="font-mono font-medium">$500.00</div>
                            </div>
                        </div>

                        {/* ACTION */}
                        <div className="p-6 pt-0">
                            <button
                                onClick={() => onAssetClick ? onAssetClick(asset.id) : setSelectedAsset(asset)}
                                className="w-full py-3 bg-neutral-800 hover:bg-white hover:text-black text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2 group-hover:shadow-[0_0_20px_rgba(16,185,129,0.1)]"
                            >
                                View & Invest <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* --- INVESTMENT DRAWER (Simple Modal for now) --- */}
            <AnimatePresence>
                {selectedAsset && (
                    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/80 backdrop-blur-xs p-4">
                        <motion.div
                            initial={{ opacity: 0, y: 100 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 100 }}
                            className="bg-neutral-900 border border-neutral-800 w-full max-w-lg rounded-2xl p-8 relative"
                        >
                            <button onClick={() => setSelectedAsset(null)} className="absolute top-4 right-4 text-neutral-500 hover:text-white">✕</button>

                            <div className="flex items-center gap-4 mb-6">
                                <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${selectedAsset.type === 'rwa' ? 'bg-blue-500/10 text-blue-500' : 'bg-orange-500/10 text-orange-500'}`}>
                                    {selectedAsset.type === 'rwa' ? <Building2 className="w-7 h-7" /> : <Globe className="w-7 h-7" />}
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold">{selectedAsset.name}</h2>
                                    <div className="text-neutral-400 text-sm">Subscription Round A</div>
                                </div>
                            </div>

                            <div className="space-y-6">
                                <div className="p-4 bg-neutral-950 rounded-xl border border-neutral-800 space-y-4">
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-neutral-500">Current Price</span>
                                        <span className="font-mono">${selectedAsset.price.toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-neutral-500">Your USD Balance</span>
                                        <span className="font-mono text-emerald-400">${currentUser?.wallet?.balances.USD.toLocaleString()}</span>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">Investment Amount (USD)</label>
                                    <div className="relative">
                                        <span className="absolute left-4 top-3.5 text-neutral-500">$</span>
                                        <input
                                            type="number"
                                            className="w-full bg-neutral-950 border border-neutral-800 rounded-xl py-3 pl-8 pr-4 text-white focus:outline-hidden focus:border-emerald-500 font-mono text-lg"
                                            placeholder="0.00"
                                            value={investmentAmount || ''}
                                            onChange={e => setInvestmentAmount(parseFloat(e.target.value))}
                                        />
                                    </div>
                                    <div className="flex justify-between mt-2 text-xs text-neutral-500">
                                        <span>Est. Tokens: {(investmentAmount / selectedAsset.price).toFixed(4)} {selectedAsset.symbol}</span>
                                        <span>Fee: $0.00</span>
                                    </div>
                                </div>

                                <button
                                    onClick={handleInvest}
                                    disabled={processing || investmentAmount <= 0}
                                    className="w-full py-4 bg-emerald-500 text-black font-bold rounded-xl hover:bg-emerald-400 transition-colors flex items-center justify-center gap-2"
                                >
                                    {processing ? (
                                        <span className="animate-pulse">Signing Transaction...</span>
                                    ) : (
                                        <>
                                            Confirm Investment <Lock className="w-4 h-4" />
                                        </>
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}
